import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMySessions, createSession as apiCreateSession, MySession } from '../services/wahaApi';

interface SessionContextType {
  sessions: MySession[];
  activeSession: string | null;
  activeSessionData: MySession | null;
  setActiveSession: (name: string) => void;
  refreshSessions: () => Promise<void>;
  createNewSession: () => Promise<any>;
  loading: boolean;
  canCreateSession: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = (): SessionContextType => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<MySession[]>([]);
  const [activeSession, setActiveSessionState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [canCreateFromApi, setCanCreateFromApi] = useState(false);

  const refreshSessions = useCallback(async () => {
    try {
      setLoading(true);
      const { sessions: data, canCreateSession: canCreate } = await getMySessions();
      setSessions(data);
      setCanCreateFromApi(canCreate);

      const stored = localStorage.getItem('activeSession');
      const validStored = stored && data.some(s => s.sessionName === stored);

      if (validStored) {
        setActiveSessionState(stored);
      } else if (data.length === 1) {
        setActiveSessionState(data[0].sessionName);
        localStorage.setItem('activeSession', data[0].sessionName);
      } else if (data.length > 1) {
        setActiveSessionState(data[0].sessionName);
        localStorage.setItem('activeSession', data[0].sessionName);
      } else {
        setActiveSessionState(null);
        localStorage.removeItem('activeSession');
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const setActiveSession = useCallback((name: string) => {
    setActiveSessionState(name);
    localStorage.setItem('activeSession', name);
  }, []);

  const activeSessionData = sessions.find(s => s.sessionName === activeSession) || null;

  const canCreateSession = canCreateFromApi;

  const createNewSession = useCallback(async () => {
    const result = await apiCreateSession();
    await refreshSessions();
    if (result.data?.sessionName) {
      setActiveSession(result.data.sessionName);
    }
    return result;
  }, [refreshSessions, setActiveSession]);

  return (
    <SessionContext.Provider value={{
      sessions,
      activeSession,
      activeSessionData,
      setActiveSession,
      refreshSessions,
      createNewSession,
      loading,
      canCreateSession
    }}>
      {children}
    </SessionContext.Provider>
  );
};
